import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { formatDistanceToNow, addDays, format } from "date-fns";
import { Mail, Calendar, MapPin, User, Building, UserPlus, Clock, Shield, Plus } from "lucide-react";
import { TrialUserProvisioning } from "@/components/admin/TrialUserProvisioning";

interface TrialRequest {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  district: string;
  role: string;
  state: string;
  message: string;
  status: string;
  created_at: string;
}

interface TrialUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  district_name?: string;
  trial_end_date?: string;
  subscription_status: string;
  created_at: string;
}

export default function TrialRequests() {
  const [requests, setRequests] = useState<TrialRequest[]>([]);
  const [trialUsers, setTrialUsers] = useState<TrialUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [provisioningDialog, setProvisioningDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<TrialRequest | null>(null);
  const [trialDuration, setTrialDuration] = useState("30");
  const [extendDialog, setExtendDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TrialUser | null>(null);
  const [extensionDays, setExtensionDays] = useState("30");
  const [isUberAdmin, setIsUberAdmin] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      // Check if uber admin using RPC
      const { data: isUber } = await supabase.rpc('is_uber_admin');
      setIsUberAdmin(!!isUber);
      
      if (isUber) {
        fetchTrialRequests();
        fetchTrialUsers();
      } else {
        setLoading(false);
      }
    };
    
    checkAccess();
  }, [user]);

  const fetchTrialRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('trial_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Error fetching trial requests:', error);
      toast.error('Failed to load trial requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrialUsers = async () => {
    try {
      // Get all users with their profile info and district details
      const { data: usersWithDistricts, error: usersError } = await supabase
        .from('profiles')
        .select(`
          id,
          email,
          first_name,
          last_name,
          district_id,
          created_at,
          districts!inner(
            name,
            trial_end_date,
            subscription_status
          )
        `)
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;

      // Transform the data
      const transformedData = (usersWithDistricts || []).map(user => ({
        id: user.id,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        district_name: user.districts?.name,
        trial_end_date: user.districts?.trial_end_date,
        subscription_status: user.districts?.subscription_status || 'trial',
        created_at: user.created_at
      }));

      setTrialUsers(transformedData);
    } catch (error) {
      console.error('Error fetching trial users:', error);
      toast.error('Failed to load trial users');
    }
  };

  const updateRequestStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from('trial_requests')
        .update({ status })
        .eq('id', id);

      if (error) throw error;

      setRequests(prev => 
        prev.map(req => 
          req.id === id ? { ...req, status } : req
        )
      );
      
      toast.success('Request status updated');
    } catch (error) {
      console.error('Error updating request status:', error);
      toast.error('Failed to update request status');
    }
  };

  const handleProvisioningComplete = () => {
    fetchTrialUsers();
    fetchTrialRequests();
  };

  const toggleTrialStatus = async (userId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'trial' ? 'expired' : 'trial';
      
      // Find user's district and update it
      const user = trialUsers.find(u => u.id === userId);
      if (!user?.district_name) {
        toast.error('User district not found');
        return;
      }

      const { error } = await supabase
        .from('districts')
        .update({ 
          subscription_status: newStatus,
          ...(newStatus === 'trial' && { trial_end_date: addDays(new Date(), 30).toISOString() })
        })
        .eq('name', user.district_name);

      if (error) throw error;

      toast.success(`Trial ${newStatus === 'trial' ? 'enabled' : 'disabled'} for ${user.first_name} ${user.last_name}`);
      fetchTrialUsers();
    } catch (error) {
      console.error('Error toggling trial status:', error);
      toast.error('Failed to update trial status');
    }
  };

  const extendTrial = async () => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      
      // Calculate new end date based on current end date or today
      const currentEndDate = selectedUser.trial_end_date ? new Date(selectedUser.trial_end_date) : new Date();
      const baseDate = currentEndDate > new Date() ? currentEndDate : new Date();
      const newEndDate = addDays(baseDate, parseInt(extensionDays));

      const { error } = await supabase
        .from('districts')
        .update({ 
          subscription_status: 'trial',
          trial_end_date: newEndDate.toISOString()
        })
        .eq('name', selectedUser.district_name);

      if (error) throw error;

      toast.success(`Trial extended for ${selectedUser.first_name} ${selectedUser.last_name} until ${format(newEndDate, 'MMM dd, yyyy')}`);
      setExtendDialog(false);
      setSelectedUser(null);
      fetchTrialUsers();
    } catch (error) {
      console.error('Error extending trial:', error);
      toast.error('Failed to extend trial');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      case 'trial_started': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTrialStatusColor = (status: string, trialEndDate?: string) => {
    if (status === 'expired' || (trialEndDate && new Date(trialEndDate) < new Date())) {
      return 'bg-red-100 text-red-800';
    }
    if (status === 'trial') {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-blue-100 text-blue-800';
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">Loading trial requests...</div>
      </div>
    );
  }

  if (!isUberAdmin) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="text-center py-8">
            <Shield className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h2 className="text-2xl font-semibold mb-4">Access Denied</h2>
            <p className="text-muted-foreground mb-4">
              You don't have uber admin permission to view this page.
            </p>
            <p className="text-sm text-muted-foreground">
              Current user: {user?.email || 'Not logged in'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Trial Management</h1>
          <p className="text-muted-foreground">
            Manage trial requests and provision trial users
          </p>
        </div>
        <Button onClick={() => { fetchTrialRequests(); fetchTrialUsers(); }} variant="outline">
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="requests" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="requests">Trial Requests</TabsTrigger>
          <TabsTrigger value="users">Trial Users</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Requests</CardTitle>
                <div className="text-2xl font-bold">{requests.length}</div>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
                <div className="text-2xl font-bold text-yellow-600">
                  {requests.filter(r => r.status === 'pending').length}
                </div>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Contacted</CardTitle>
                <div className="text-2xl font-bold text-blue-600">
                  {requests.filter(r => r.status === 'contacted').length}
                </div>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Active Trials</CardTitle>
                <div className="text-2xl font-bold text-green-600">
                  {requests.filter(r => r.status === 'trial_started').length}
                </div>
              </CardHeader>
            </Card>
          </div>

          {/* Requests Table */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Trial Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {requests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No trial requests found.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Contact</TableHead>
                        <TableHead>District & Role</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                {request.first_name} {request.last_name}
                              </div>
                              <div className="text-sm text-muted-foreground flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                <a href={`mailto:${request.email}`} className="hover:underline">
                                  {request.email}
                                </a>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium flex items-center gap-2">
                                <Building className="h-4 w-4 text-muted-foreground" />
                                {request.district}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {request.role}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              {request.state}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(request.status)}>
                              {request.status.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Calendar className="h-4 w-4" />
                              {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Select
                                value={request.status}
                                onValueChange={(value) => updateRequestStatus(request.id, value)}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="contacted">Contacted</SelectItem>
                                  <SelectItem value="trial_started">Trial Started</SelectItem>
                                  <SelectItem value="closed">Closed</SelectItem>
                                </SelectContent>
                              </Select>
                              
                              {request.status === 'contacted' && (
                                <Button 
                                  size="sm" 
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setProvisioningDialog(true);
                                  }}
                                  className="whitespace-nowrap"
                                >
                                  <UserPlus className="h-4 w-4 mr-2" />
                                  Provision Trial
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Request Details */}
          {requests.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recent Messages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {requests
                    .filter(r => r.message && r.message.trim())
                    .slice(0, 5)
                    .map((request) => (
                      <div key={request.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-medium">
                            {request.first_name} {request.last_name} - {request.district}
                          </div>
                          <Badge className={getStatusColor(request.status)} variant="secondary">
                            {request.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {request.message}
                        </p>
                        <div className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          {/* Trial Users Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Trial Users</CardTitle>
                <div className="text-2xl font-bold">{trialUsers.length}</div>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Active Trials</CardTitle>
                <div className="text-2xl font-bold text-green-600">
                  {trialUsers.filter(u => u.subscription_status === 'trial' && (!u.trial_end_date || new Date(u.trial_end_date) > new Date())).length}
                </div>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Expired Trials</CardTitle>
                <div className="text-2xl font-bold text-red-600">
                  {trialUsers.filter(u => u.subscription_status === 'expired' || (u.trial_end_date && new Date(u.trial_end_date) < new Date())).length}
                </div>
              </CardHeader>
            </Card>
          </div>

          {/* Trial Users Table */}
          <Card>
            <CardHeader>
              <CardTitle>Trial Users</CardTitle>
            </CardHeader>
            <CardContent>
              {trialUsers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No trial users found.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>District</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Trial End Date</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trialUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-sm text-muted-foreground flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                {user.email}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building className="h-4 w-4 text-muted-foreground" />
                              {user.district_name || 'No District'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getTrialStatusColor(user.subscription_status, user.trial_end_date)}>
                              {user.subscription_status === 'trial' && user.trial_end_date && new Date(user.trial_end_date) < new Date() 
                                ? 'Expired' 
                                : user.subscription_status
                              }
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              {user.trial_end_date 
                                ? format(new Date(user.trial_end_date), 'MMM dd, yyyy')
                                : 'No expiration'
                              }
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-muted-foreground">
                              {formatDistanceToNow(new Date(user.created_at), { addSuffix: true })}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toggleTrialStatus(user.id, user.subscription_status)}
                              >
                                {user.subscription_status === 'trial' ? 'Disable' : 'Enable'} Trial
                              </Button>
                              
                              <Dialog open={extendDialog && selectedUser?.id === user.id} onOpenChange={setExtendDialog}>
                                <DialogTrigger asChild>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => setSelectedUser(user)}
                                  >
                                    <Clock className="h-4 w-4 mr-1" />
                                    Extend
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Extend Trial</DialogTitle>
                                    <DialogDescription>
                                      Extend trial for {user.first_name} {user.last_name}
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    <div className="space-y-2">
                                      <Label htmlFor="extension-days">Extension (days)</Label>
                                      <Input
                                        id="extension-days"
                                        type="number"
                                        value={extensionDays}
                                        onChange={(e) => setExtensionDays(e.target.value)}
                                        min="1"
                                        max="365"
                                      />
                                    </div>
                                    <div className="flex justify-end gap-2">
                                      <Button variant="outline" onClick={() => setExtendDialog(false)}>
                                        Cancel
                                      </Button>
                                      <Button onClick={extendTrial} disabled={loading}>
                                        Extend Trial
                                      </Button>
                                    </div>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Trial User Provisioning Dialog */}
      <TrialUserProvisioning
        open={provisioningDialog}
        onOpenChange={setProvisioningDialog}
        request={selectedRequest}
        trialDuration={trialDuration}
        onTrialDurationChange={setTrialDuration}
        onProvisionComplete={handleProvisioningComplete}
      />
    </div>
  );
}